/**
 * BridgeBadge — iter-235
 *
 * Compact studio-header badge for the ELPM Studio Bridge:
 *   - Shows current connection state (Disconnected / Connecting / ON-AIR)
 *   - Click → opens a small popover with:
 *       • Generate session code button
 *       • Copy code to clipboard
 *       • Auto-connect toggle (persisted)
 *       • Inline link to the bridge README
 *
 * Calls the bridgeClient singleton (lib/bridgeClient.js). The transport
 * event broadcasting is wired separately inside the engine play/pause
 * handlers so this badge never has to know about the audio engine.
 */
import { useEffect, useState } from "react";
import { Plug, Copy, Sparkles, X, RefreshCw, ExternalLink, Activity, ArrowDownToLine, Music2, Download } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  connect,
  disconnect,
  generateNewCode,
  getAutoConnect,
  getLatencyOffset,
  getMidiSync,
  getReverseSync,
  getSavedCode,
  onBridgeVersion,
  setAutoConnect,
  setLatencyOffset,
  setMidiSync,
  setReverseSync,
  subscribe,
} from "@/lib/bridgeClient";
import compareSemver from "@/lib/compareSemver";
import BridgeHealthModal from "@/components/daw/BridgeHealthModal";

export default function BridgeBadge() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState({ code: null, state: "closed" });
  const [code, setCode] = useState(() => getSavedCode());
  const [auto, setAuto] = useState(() => getAutoConnect());
  const [latency, setLatency] = useState(() => getLatencyOffset());
  // iter-240 — Reverse Sync toggle (DAW → ELPM transport mirroring).
  const [reverse, setReverse] = useState(() => getReverseSync());
  // iter-243 — MIDI Sync toggle (CC + Note bidirectional bridging).
  const [midiSync, setMidiSyncState] = useState(() => getMidiSync());
  // iter-237 — Tap-to-tune state. The user taps a button repeatedly
  // while listening to Ableton's downbeat; we compute the phase offset
  // between their taps and ELPM's beat grid and surface a suggested
  // latency value they can Apply.
  const [tapping, setTapping] = useState(false);
  const [taps, setTaps] = useState([]);     // array of Date.now() per tap
  const [tapSuggestion, setTapSuggestion] = useState(null); // ms, null = not yet computed

  // iter-258 — Bridge version tracking. `peerVersion` is whatever the
  // connected desktop bridge reported in its `hello` handshake;
  // `latestVersion` is what `/api/bridge/version` says is available
  // for download. When peerVersion < latestVersion, the popover shows
  // an "Update available" pill with a one-click download CTA.
  const [peerVersion, setPeerVersion] = useState(null);
  const [latestVersion, setLatestVersion] = useState(null);
  // iter-259 — Bridge Health modal open state. Opened from a small
  // "Health" button below the connect CTA when the bridge is live.
  const [healthOpen, setHealthOpen] = useState(false);

  // Subscribe to bridgeClient state.
  useEffect(() => subscribe(setStatus), []);

  // iter-258 — Subscribe to the peer's `hello` advertisement.
  useEffect(() => onBridgeVersion(({ version }) => setPeerVersion(version)), []);

  // iter-258 — Fetch the latest available bridge version on mount so
  // we have something to compare against the moment the peer connects.
  // Cheap call (returns a single string), guarded by AbortController
  // so an unmount during the fetch doesn't trigger a setState warning.
  useEffect(() => {
    const ac = new AbortController();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bridge/version`, { signal: ac.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data?.version) setLatestVersion(String(data.version)); })
      .catch(() => { /* offline / 404 — pill just won't show */ });
    return () => ac.abort();
  }, []);

  // Auto-connect on mount if the user opted in last time + we have a code.
  useEffect(() => {
    if (auto && code && status.state === "closed") {
      try { connect(code); } catch (e) { void e; }
    }
    // Mount-only by design: re-running on auto/code/status changes would
    // re-fire connect() mid-session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = async () => {
    try {
      const c = await generateNewCode();
      setCode(c);
      toast.success(`Session code: ${c}`, { description: "Paste it into the desktop bridge CLI." });
    } catch (e) {
      toast.error("Couldn't generate session code", { description: e.message });
    }
  };

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copied to clipboard");
    } catch (e) {
      toast.error("Copy failed", { description: e.message });
    }
  };

  // iter-258 — Update detection. Only flag stale when both versions
  // are known AND peer is strictly older. Anything else (unknown,
  // newer-than-server pre-release) stays silent.
  const updateAvailable = peerVersion && latestVersion
    && compareSemver(peerVersion, latestVersion) < 0;

  // iter-257 — One-click download of the desktop-bridge source zip.
  // Streamed from `/api/bridge/download` so we always serve the latest
  // committed version. Triggers a hidden anchor click instead of
  // window.open so the popover stays open after the download.
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bridge/download`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Best-effort version stamp from the response header — used in
      // the success toast so users can verify they got what they
      // expected even if their browser renamed the file.
      const version = res.headers.get("x-bridge-version") || "";
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = version ? `elpm-bridge-v${version}.zip` : "elpm-bridge.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Bridge downloaded${version ? ` (v${version})` : ""}`, {
        description: "Unzip → run `yarn install && yarn start`. INSTALL.md inside walks you through the rest.",
        duration: 9000,
      });
    } catch (e) {
      toast.error("Couldn't download the bridge", { description: e.message });
    } finally {
      setDownloading(false);
    }
  };

  const handleConnectToggle = () => {
    if (status.state === "open" || status.state === "connecting") {
      disconnect();
      return;
    }
    try {
      connect(code);
    } catch (e) {
      toast.error("Couldn't connect", { description: e.message });
    }
  };

  // iter-237 — Tap-to-tune. Reads the engine singleton at compute
  // time (no prop plumbing) and uses ELPM's wall-clock play start +
  // BPM to derive the phase offset of the user's taps.
  const handleTap = () => {
    const engine = (typeof window !== "undefined") ? (window.__elpmEngine || window.elpmEngine) : null;
    if (!engine?.playing || !engine.playStartedAtMs) {
      toast.message("Press Play in ELPM first", {
        description: "Tap-to-tune needs both ELPM AND Ableton playing so it can measure the offset between them.",
      });
      return;
    }
    setTapping(true);
    const now = Date.now();
    const nextTaps = [...taps, now];
    setTaps(nextTaps);
    if (nextTaps.length >= 4) {
      const bpm = engine.tempo || 120;
      const beatMs = 60000 / bpm;
      const startedMs = engine.playStartedAtMs;
      // For each tap: where it lands in the current beat (0 = on-beat).
      // We center around the closest beat (signed phase in [-beatMs/2, +beatMs/2]).
      const phases = nextTaps.map((t) => {
        const inBeat = ((t - startedMs) % beatMs + beatMs) % beatMs;
        return inBeat > beatMs / 2 ? inBeat - beatMs : inBeat;
      });
      // Median (robust to a missed tap) — Ableton beat lands LATER than
      // ELPM's beat by the median phase. If positive, that's our offset.
      const sorted = [...phases].sort((a, b) => a - b);
      const median = sorted[Math.floor(sorted.length / 2)];
      const clamped = Math.max(0, Math.min(300, Math.round(median)));
      setTapSuggestion(clamped);
      setTapping(false);
    }
  };

  const handleApplyTap = () => {
    if (typeof tapSuggestion !== "number") return;
    const v = setLatencyOffset(tapSuggestion);
    setLatency(v);
    toast.success(`Latency set to ${v} ms`, {
      description: "Sounded too tight or loose? Tap again or fine-tune with the slider.",
    });
    setTaps([]);
    setTapSuggestion(null);
  };

  const handleResetTap = () => {
    setTaps([]);
    setTapSuggestion(null);
    setTapping(false);
  };

  const live = status.state === "open";
  const connecting = status.state === "connecting";

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="bridge-badge"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider transition ${
          live
            ? "bg-emerald-500/20 text-emerald-100 ring-1 ring-emerald-300/55 shadow-[0_0_12px_rgba(16,185,129,0.4)]"
            : connecting
            ? "bg-amber-500/20 text-amber-100 ring-1 ring-amber-300/55"
            : "bg-white/[0.06] text-white/65 hover:bg-white/[0.12] hover:text-white"
        }`}
        title="ELPM Studio Bridge — sync transport to your native DAW"
      >
        <Plug className="h-3 w-3" />
        {live ? "Bridge ON-AIR" : connecting ? "Connecting…" : "Bridge"}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            data-testid="bridge-popover"
            className="absolute right-0 top-full z-50 mt-2 w-80 rounded-2xl border border-white/[0.08] bg-zinc-950/95 p-4 shadow-[0_24px_48px_-16px_rgba(0,0,0,0.6)] backdrop-blur"
          >
            <div className="mb-3 flex items-center gap-2">
              <span className="grid h-8 w-8 place-items-center rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-400 text-zinc-950">
                <Plug className="h-4 w-4" />
              </span>
              <div className="flex-1">
                <h3 className="font-display text-sm font-semibold text-white">Desktop Bridge</h3>
                <p className="font-mono text-[10px] uppercase tracking-wider text-white/45">
                  Sync transport to Ableton / Logic / FL Studio
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-full bg-white/[0.06] p-1 text-white/55 hover:bg-white/[0.14] hover:text-white"
                aria-label="Close"
              >
                <X className="h-3 w-3" />
              </button>
            </div>

            {/* Session code row */}
            <div className="mb-2 rounded-xl border border-white/[0.06] bg-black/40 px-3 py-2">
              <div className="mb-1 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-wider text-white/55">Session code</span>
                <button
                  type="button"
                  data-testid="bridge-generate"
                  onClick={handleGenerate}
                  className="flex items-center gap-1 rounded-full bg-fuchsia-500/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-fuchsia-100 hover:bg-fuchsia-500/35"
                >
                  <Sparkles className="h-2.5 w-2.5" />
                  Generate
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <input
                  data-testid="bridge-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toLowerCase())}
                  placeholder="Paste a code or hit Generate"
                  className="flex-1 rounded-md bg-black/40 px-2 py-1 font-mono text-[12px] tracking-wider text-emerald-200 outline-none ring-1 ring-white/[0.08] focus:ring-emerald-400/40"
                />
                <button
                  type="button"
                  data-testid="bridge-copy"
                  onClick={handleCopy}
                  disabled={!code}
                  className="grid h-7 w-7 place-items-center rounded-md bg-white/[0.06] text-white/65 transition hover:bg-emerald-500/25 hover:text-white disabled:opacity-40"
                  title="Copy to clipboard"
                >
                  <Copy className="h-3 w-3" />
                </button>
              </div>
            </div>

            {/* Connect button */}
            <button
              type="button"
              data-testid="bridge-connect"
              onClick={handleConnectToggle}
              disabled={!code || connecting}
              className={`mb-2 w-full rounded-xl px-3 py-2 font-mono text-[11px] uppercase tracking-wider transition ${
                live
                  ? "bg-rose-500/20 text-rose-100 ring-1 ring-rose-400/40 hover:bg-rose-500/35"
                  : "bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-[0_0_18px_rgba(16,185,129,0.35)] hover:opacity-95 disabled:opacity-40"
              }`}
            >
              {live ? "Disconnect" : connecting ? "Connecting…" : <span className="inline-flex items-center gap-1.5"><RefreshCw className="h-3 w-3" />Connect</span>}
            </button>

            {/* iter-258 — Update-available banner. Only shown when the
                connected bridge advertised a version strictly older
                than what the backend has available for download.
                Single tap re-uses handleDownload so the user can
                grab the new zip without scrolling. */}
            {updateAvailable && (
              <div
                data-testid="bridge-update-pill"
                className="mb-2 flex items-center justify-between gap-2 rounded-xl border border-amber-300/30 bg-amber-500/[0.08] px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-amber-100"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3 text-amber-200" />
                  Update available
                  <span className="font-mono text-[9px] tracking-wider text-amber-200/70">
                    v{peerVersion} → v{latestVersion}
                  </span>
                </span>
                <button
                  type="button"
                  data-testid="bridge-update-now"
                  onClick={handleDownload}
                  disabled={downloading}
                  className="inline-flex items-center gap-1 rounded-md bg-amber-400/20 px-2 py-1 text-[10px] uppercase tracking-wider text-amber-50 transition hover:bg-amber-400/35 disabled:opacity-50"
                >
                  <Download className="h-3 w-3" />
                  {downloading ? "…" : "Get it"}
                </button>
              </div>
            )}

            {/* iter-257 — One-click download of the desktop-bridge zip.
                Sits below the Connect button so first-time users see
                this CTA the moment they realise they need the local
                companion. Includes INSTALL.md inside the zip so the
                user doesn't have to come back to the docs page. */}
            <button
              type="button"
              data-testid="bridge-download"
              onClick={handleDownload}
              disabled={downloading}
              className="mb-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-300/30 bg-emerald-500/[0.08] px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-emerald-100 transition hover:border-emerald-300/60 hover:bg-emerald-500/[0.18] disabled:opacity-50"
              title="Grab the desktop bridge — small Node CLI that links the browser to Ableton / Logic / FL Studio / Reaper"
            >
              <Download className="h-3 w-3" />
              {downloading ? "Packing…" : "Download desktop bridge"}
              <span className="font-mono text-[9px] tracking-wider text-emerald-300/70">.zip</span>
            </button>

            {/* iter-259 — Open the Bridge Health Dashboard. Hidden
                until a session code exists since there's nothing
                meaningful to display before the user has at least
                generated a code. */}
            {code && (
              <button
                type="button"
                data-testid="bridge-health-open"
                onClick={() => setHealthOpen(true)}
                className="mb-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-cyan-300/25 bg-cyan-500/[0.06] px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-cyan-100 transition hover:border-cyan-300/55 hover:bg-cyan-500/[0.14]"
              >
                <Activity className="h-3 w-3" />
                Open bridge health
              </button>
            )}

            {/* Auto-connect toggle */}
            <label className="mb-3 flex cursor-pointer items-center justify-between rounded-lg bg-white/[0.04] px-2.5 py-1.5">
              <span className="font-mono text-[10px] uppercase tracking-wider text-white/65">Auto-connect on load</span>
              <input
                type="checkbox"
                data-testid="bridge-autoconnect"
                checked={auto}
                onChange={(e) => { setAuto(e.target.checked); setAutoConnect(e.target.checked); }}
                className="h-3 w-3 cursor-pointer accent-emerald-400"
              />
            </label>

            {/* iter-240 — Reverse Sync (DAW → ELPM). When ON, transport
                events from the DAW (via the bridge's MIDI input port)
                drive ELPM's engine. Off by default; user must opt in. */}
            <label
              className={`mb-3 flex cursor-pointer items-start justify-between gap-3 rounded-lg border px-2.5 py-2 transition ${
                reverse
                  ? "border-fuchsia-400/40 bg-fuchsia-500/[0.06]"
                  : "border-white/[0.06] bg-white/[0.03] hover:bg-white/[0.05]"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <ArrowDownToLine className={`h-3 w-3 ${reverse ? "text-fuchsia-300" : "text-white/55"}`} />
                  <span className={`font-mono text-[10px] uppercase tracking-wider ${reverse ? "text-fuchsia-100" : "text-white/65"}`}>
                    Reverse Sync · DAW → ELPM
                  </span>
                </div>
                <p className="mt-1 text-[10px] leading-snug text-white/45">
                  Bridge listens for MMC/start/stop from your DAW and plays ELPM in lockstep. Requires <code className="text-fuchsia-200">--input-port</code> on the desktop bridge.
                </p>
              </div>
              <input
                type="checkbox"
                data-testid="bridge-reverse-sync"
                checked={reverse}
                onChange={(e) => {
                  setReverse(e.target.checked);
                  setReverseSync(e.target.checked);
                  if (e.target.checked) {
                    toast.success("Reverse Sync ON", { description: "DAW transport will now drive ELPM playback." });
                  } else {
                    toast.message("Reverse Sync OFF");
                  }
                }}
                className="mt-1 h-3 w-3 flex-shrink-0 cursor-pointer accent-fuchsia-400"
              />
            </label>

            {/* iter-243 — MIDI Sync (Phase 3 extended MIDI). Independent
                of Reverse Sync. When ON: master volume ↔ CC#7 and MIDI
                notes flow both ways. Power users mix-and-match. */}
            <label
              className={`mb-3 flex cursor-pointer items-start justify-between gap-3 rounded-lg border px-2.5 py-2 transition ${
                midiSync
                  ? "border-cyan-400/40 bg-cyan-500/[0.06]"
                  : "border-white/[0.06] bg-white/[0.03] hover:bg-white/[0.05]"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <Music2 className={`h-3 w-3 ${midiSync ? "text-cyan-300" : "text-white/55"}`} />
                  <span className={`font-mono text-[10px] uppercase tracking-wider ${midiSync ? "text-cyan-100" : "text-white/65"}`}>
                    MIDI Sync · CC + Notes
                  </span>
                </div>
                <p className="mt-1 text-[10px] leading-snug text-white/45">
                  Master vol ↔ <code className="text-cyan-200">CC#7</code>. MIDI notes route to drum machine / bass player. Wire <code className="text-cyan-200">--input-port</code> on the desktop bridge.
                </p>
              </div>
              <input
                type="checkbox"
                data-testid="bridge-midi-sync"
                checked={midiSync}
                onChange={(e) => {
                  setMidiSyncState(e.target.checked);
                  setMidiSync(e.target.checked);
                  if (e.target.checked) {
                    toast.success("MIDI Sync ON", { description: "Master volume & MIDI notes now flow both ways." });
                  } else {
                    toast.message("MIDI Sync OFF");
                  }
                }}
                className="mt-1 h-3 w-3 flex-shrink-0 cursor-pointer accent-cyan-400"
              />
            </label>

            {/* iter-237 — Tap-to-tune. User taps along with what they
                HEAR from Ableton; we compute the median phase offset
                from ELPM's beat grid and offer one-click Apply. */}
            <div className="mb-3 rounded-lg border border-emerald-400/20 bg-emerald-500/[0.04] px-2.5 py-2">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-emerald-100">
                  <Activity className="h-3 w-3" /> Tap to tune
                </span>
                {taps.length > 0 && (
                  <button
                    type="button"
                    data-testid="bridge-tap-reset"
                    onClick={handleResetTap}
                    className="font-mono text-[9px] uppercase tracking-wider text-white/55 hover:text-white"
                  >
                    Reset
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="bridge-tap-btn"
                  onClick={handleTap}
                  className={`flex-1 rounded-md px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider transition ${
                    tapping || taps.length > 0
                      ? "bg-emerald-500/30 text-white ring-1 ring-emerald-300/55"
                      : "bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/30"
                  }`}
                >
                  {tapSuggestion !== null ? `Tap ${taps.length}/4 — done` : `Tap ${taps.length}/4 with Ableton`}
                </button>
                {tapSuggestion !== null && (
                  <button
                    type="button"
                    data-testid="bridge-tap-apply"
                    onClick={handleApplyTap}
                    className="rounded-md bg-gradient-to-r from-emerald-500 to-cyan-500 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white shadow-[0_0_12px_rgba(16,185,129,0.4)] hover:opacity-95"
                  >
                    Apply {tapSuggestion} ms
                  </button>
                )}
              </div>
              <p className="mt-1 text-[9px] leading-snug text-white/45">
                Press Play in ELPM, then tap to Ableton&apos;s downbeat 4 times.
              </p>
            </div>

            {/* iter-236 — Latency offset slider. Delays ELPM's own
                audio start by N ms while the bridge fires MMC + Clock
                immediately, so Ableton's natural buffer lag catches
                up. Tune by ear: tap your foot, hit Play, adjust until
                ELPM and Ableton land on the same downbeat. */}
            <div className="mb-3 rounded-lg bg-white/[0.04] px-2.5 py-2">
              <div className="mb-1 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-wider text-white/65">
                  Latency offset
                </span>
                <span
                  data-testid="bridge-latency-value"
                  className="font-mono text-[11px] text-emerald-200"
                >
                  {latency} ms
                </span>
              </div>
              <input
                data-testid="bridge-latency"
                type="range"
                min={0}
                max={300}
                step={1}
                value={latency}
                onChange={(e) => {
                  const v = setLatencyOffset(e.target.value);
                  setLatency(v);
                }}
                className="w-full accent-emerald-400"
              />
              <p className="mt-1 text-[9px] leading-snug text-white/45">
                Delays ELPM playback by N ms so Ableton&apos;s buffer lag catches up. Tune by ear — typically 5–30 ms.
              </p>
            </div>

            {/* Setup help */}
            <details className="rounded-lg bg-white/[0.03] px-2.5 py-1.5">
              <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-wider text-white/55">
                How to set up Ableton →
              </summary>
              <ol className="mt-2 list-decimal space-y-1 pl-4 text-[11px] leading-relaxed text-white/70">
                <li>Install the bridge: <code className="rounded bg-black/40 px-1 font-mono text-[10px] text-emerald-200">cd /app/bridge && yarn install && yarn start</code></li>
                <li>Paste the code above into the bridge CLI prompt.</li>
                <li>In Ableton: Preferences → Link Tempo MIDI → enable <strong>Sync</strong> + <strong>Remote</strong> on <code className="font-mono text-[10px]">ELPM Bridge</code>.</li>
                <li>Set Ableton transport to <strong>EXT</strong>.</li>
                <li>Hit Play in ELPM Studio — Ableton follows.</li>
              </ol>
              <a
                href="https://github.com"
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-[10px] text-fuchsia-300 hover:text-fuchsia-200"
              >
                Full README <ExternalLink className="h-2.5 w-2.5" />
              </a>
            </details>
          </motion.div>
        )}
      </AnimatePresence>
      {/* iter-259 — Bridge Health dashboard. Rendered as a sibling of
          the popover so it survives popover close + can be opened
          from the badge directly via keyboard shortcut later. */}
      <BridgeHealthModal
        open={healthOpen}
        onClose={() => setHealthOpen(false)}
        sessionCode={code}
        live={live}
      />
    </div>
  );
}
